import React from 'react';
import styled from 'styled-components';

// --- Global Styles ---
const GlobalStyleContainer = styled.div`
  font-family: Arial, sans-serif;
  color: #333;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  background-color: #f7f7f7; // Light grey background
`;

// --- Benefits Section (Top) ---
const BenefitsSection = styled.section`
  padding: 40px 5%;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  background-color: white;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
  margin-bottom: 30px;
  border-radius: 8px;
`;

const BenefitsTitle = styled.h1`
  font-size: 2em;
  color: #333;
  margin-bottom: 10px;
`;

const BenefitsSubtitle = styled.p`
  font-size: 1.1em;
  color: #666;
  margin-bottom: 30px;
`;

const BenefitsText = styled.p`
  font-size: 0.95em;
  line-height: 1.6;
  color: #555;
  margin-bottom: 40px;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
`;

// --- Local SEO Packages Section ---
const PackagesSection = styled.section`
  padding: 40px 5%;
  max-width: 1200px;
  margin: 0 auto 30px auto;
  background-color: white;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
  border-radius: 8px;
`;

const PackagesTitle = styled.h2`
  text-align: center;
  font-size: 1.8em;
  color: #333;
  margin-bottom: 30px;
`;

const PackagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const PackageCard = styled.div`
  background-color: #ffe0b2; /* Light orange */
  border-radius: 8px;
  padding: 30px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 250px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const PackageIcon = styled.div`
  font-size: 3em;
  color: #ff9800; /* Orange icon */
  margin-bottom: 15px;
`;

const PackageName = styled.h3`
  font-size: 1.4em;
  color: #333;
  margin-bottom: 10px;
`;

const PackagePrice = styled.p`
  font-size: 1.8em;
  font-weight: bold;
  color: #e65100; /* Darker orange price */
  margin-bottom: 5px;
`;

const PackageDuration = styled.p`
  font-size: 0.9em;
  color: #666;
  margin-bottom: 20px;
`;

const PackageBenefit = styled.p`
  font-size: 0.9em;
  color: #444;
  margin-bottom: 25px;
  flex-grow: 1; /* Allows card content to push button down */
`;

const ReadMoreButton = styled.button`
  background-color: #ff9800;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1em;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #e68a00;
  }
`;

// --- How Can Local SEO Help You Section ---
const HowLocalSEOHelpsSection = styled.section`
  background-color: #333; // Dark background
  color: white;
  padding: 40px 5%;
  margin-bottom: 30px;
`;

const HowLocalSEOHelpsTitle = styled.h2`
  text-align: center;
  font-size: 2em;
  margin-bottom: 10px;
  color: white;
`;

const HowLocalSEOHelpsDescription = styled.p`
  text-align: center;
  font-size: 1em;
  color: #ccc;
  margin-bottom: 40px;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
`;

const HelpCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 20px;
`;

const HelpCard = styled.div`
  background-color: #444; // Slightly lighter dark for cards
  border-radius: 8px;
  padding: 30px;
  display: flex;
  align-items: flex-start;
  gap: 20px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
`;

const HelpCardIcon = styled.div`
  font-size: 2.5em;
  color: #00bcd4; // Teal accent color
  flex-shrink: 0;
`;

const HelpCardContent = styled.div``;

const HelpCardTitle = styled.h3`
  font-size: 1.3em;
  color: #00bcd4;
  margin-bottom: 10px;
`;

const HelpCardText = styled.p`
  font-size: 0.95em;
  line-height: 1.6;
  color: #bbb;
`;

// --- How Does It Work Section (Bottom) ---
const HowItWorksSection = styled.section`
  background-color: #000; // Black background for the final section
  color: white;
  padding: 60px 5%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const HowItWorksHeader = styled.div`
  position: relative;
  margin-bottom: 40px;
`;

const HowItWorksTitle = styled.h2`
  font-size: 2.5em;
  color: white;
  margin: 0;
  position: relative;
  z-index: 2;
`;

const TitleUnderline = styled.div`
  position: absolute;
  bottom: -5px;
  left: 0;
  right: 0;
  height: 5px;
  background-color: #e74c3c; /* Red underline for emphasis */
  z-index: 1;
`;

const LocalSEOImage = styled.img`
  width: 200px; /* Adjust as needed */
  height: auto;
  margin-bottom: 30px;
`;

const HowItWorksDescriptionBlock = styled.div`
  background-color: #222;
  padding: 30px;
  border-radius: 8px;
  max-width: 800px;
  text-align: left;
  line-height: 1.7;
  font-size: 1em;
  color: #eee;
  box-shadow: 0 8px 16px rgba(0,0,0,0.4);
`;

const HowItWorksButton = styled.button`
  background-color: #e74c3c; /* Red button */
  color: white;
  border: none;
  padding: 12px 25px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1.1em;
  margin-top: 30px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #c0392b;
  }
`;

// --- Main Page Component ---
const LocalSEO2 = () => {
  const companyName = "SRK Software"; // As requested

  const packages = [
    { name: "SMALL BUSINESS", price: "12,000 INR/150 USD Monthly", duration: "Best For Small Business", benefit: "Boost your online presence and reach local customers effectively." },
    { name: "MIDSIZE BUSINESS", price: "15,000 INR/220 USD Monthly", duration: "Best For Midsize Business", benefit: "Comprehensive local SEO for growing businesses with advanced needs." },
    { name: "ENTERPRISE BUSINESS", price: "24,000 INR/350 USD Monthly", duration: "Best For Enterprise Business", benefit: "Customized enterprise-level local SEO strategies for large businesses." },
  ];

  const helpPoints = [
    { icon: "📍", title: "Let Found by Nearby Customers", text: `Local SEO makes sure your business shows up when people in your area search for products or services you offer, like putting up a big, bright billboard for shoppers on the internet.` },
    { icon: "⭐", title: "Boost Your Online Reputation", text: `When your business appears in local search results, it builds trust. Positive reviews and feedback from happy customers can make you look like the best option.` },
    { icon: "👣", title: "More Foot Traffic and Calls", text: `Local SEO doesn't just get you seen online; it can also lead to more people visiting your physical store or calling you. It's like having a consistent stream of new customers.` },
    { icon: "💸", title: "Cost-Effective Marketing", text: `Compared to traditional advertising, local SEO is cost-effective. You're reaching customers who are already looking for what you offer, so your marketing dollars go a long way.` },
  ];

  return (
    <GlobalStyleContainer>
      <BenefitsSection>
        <BenefitsTitle>Benefits of Local SEO Packages</BenefitsTitle>
        <BenefitsSubtitle>"Near me" searches have increased by more than 400%</BenefitsSubtitle>
        <BenefitsText>
          As the world of business becomes increasingly more competitive, it's important to make sure that your company is doing everything it can to get ahead. One key way to do this is by investing in local SEO. SEO, or search engine optimization, is a process by which businesses can improve their visibility and ranking on search engine results pages (SERPs). Local SEO specifically refers to the practice of optimizing a business for local searches.
        </BenefitsText>
      </BenefitsSection>

      <PackagesSection>
        <PackagesTitle>Local SEO Packages</PackagesTitle>
        <PackagesGrid>
          {packages.map((pkg, index) => (
            <PackageCard key={index}>
              <PackageIcon>🛒</PackageIcon> {/* Assuming a shopping cart like icon */}
              <PackageName>{pkg.name}</PackageName>
              <PackagePrice>{pkg.price}</PackagePrice>
              <PackageDuration>{pkg.duration}</PackageDuration>
              <PackageBenefit>{pkg.benefit}</PackageBenefit>
              <ReadMoreButton>Read More</ReadMoreButton>
            </PackageCard>
          ))}
        </PackagesGrid>
      </PackagesSection>

      <HowLocalSEOHelpsSection>
        <HowLocalSEOHelpsTitle>How can local seo help you?</HowLocalSEOHelpsTitle>
        <HowLocalSEOHelpsDescription>
          Local SEO helps businesses by increasing their visibility in local search results, attracting targeted customers, improving online reputation, and providing a cost-effective marketing solution. Unlike national or global SEO, where companies have oversight, making it essential for businesses serving specific geographic areas.
        </HowLocalSEOHelpsDescription>
        <HelpCardsGrid>
          {helpPoints.map((point, index) => (
            <HelpCard key={index}>
              <HelpCardIcon>{point.icon}</HelpCardIcon>
              <HelpCardContent>
                <HelpCardTitle>{point.title}</HelpCardTitle>
                <HelpCardText>{point.text}</HelpCardText>
              </HelpCardContent>
            </HelpCard>
          ))}
        </HelpCardsGrid>
      </HowLocalSEOHelpsSection>

      <HowItWorksSection>
        <HowItWorksHeader>
          <HowItWorksTitle>How does it work?</HowItWorksTitle>
          <TitleUnderline />
        </HowItWorksHeader>
        <LocalSEOImage src="https://via.placeholder.com/200x100/333333/ffffff?text=Local+SEO+Logo" alt="Local SEO" />
        <HowItWorksDescriptionBlock>
          Local SEO is like giving your business a special boost in your neighborhood. Imagine you have a fantastic store, but nobody knows about it. That's where local SEO helps with that. Here's how it works: First, we use specific words on your website and online listings that people nearby are searching for. This tells search engines like Google that your business is local. Next, we make sure your business looks great on Google Maps and other local search engines.
          <br /><br />
          We also help you get your business information in the same everywhere online, so search engines trust it. Then, we organize your website to make it easy for local customers to find exactly what they're looking for, and we encourage happy customers. All of this helps more people in your area discover your business when they search online. It's like putting up a big sign that says, "We're here, come on in!"
        </HowItWorksDescriptionBlock>
        <HowItWorksButton>We're here, come on in!</HowItWorksButton>
      </HowItWorksSection>
    </GlobalStyleContainer>
  );
};

export default LocalSEO2;

